import React, {useContext, useEffect} from "react";
import {useParams} from "react-router-dom";
import {useAuthInfo} from "@propelauth/react";
import {FormProvider, SubmitHandler, useForm} from "react-hook-form";

import {VEDetailItem, VEListItem, VESuggestion} from "../ve_model";
import {Card, CardContent, CardHeader, CardTitle} from "../../ui/card";
import {Button} from "../../ui/button";
import {Separator} from "../../ui/separator";
import {useToast} from "../../ui/use-toast";
import {TrashIcon} from "@radix-ui/react-icons";

import {deleteWithToken, fetchWithToken, postWithToken, putWithToken} from "../../../api/apiBase";
import {UserContext} from "../../../contexts/UserContext";
import {ProjectContext} from "../../../contexts/ProjectContext";
import {PriceForm, SuggestedByLine, SuggestionForm} from "../veItemViewParts";
import {DefaultScope} from "../../scope/ScopeSelector";


function defaultSuggestion(scope_id: string | null = null) {
    return {
        created_by: {name: '', email: ''},
        description: " ",
        title: " ",
        shared_state: {share_requested: false, share_enabled: false},
        scope_id: scope_id,
    }
}

function defaultVEListItem(scope_id: string | null = null) {
    return {
        suggestion: defaultSuggestion(scope_id),
        price: {savings_amount: 0, description: " "},
    }
}


type DeveloperVeItemProps = {
    suggestion_id: string | null;
    price_id: string | null;
    setItemId: (x: string) => void;
    setSuggestionId: (x: string) => void;
    updateItem: (item: VEListItem) => void;
    insertItem: (item: VEListItem) => void;
    hideItem: (item: VEListItem) => void;
    suggestionPrice: VEListItem | null;
};


/**
 * Developer VE Item Form
 *
 * States:
 *   - Create new suggestion (suggestion_id=null)
 *   - View/edit existing suggestion (suggestion_id set)
 *   - View price from a builder (read-only)
 *
 * Developer never creates or edits prices.
 */
export function DeveloperVeItemForm({
    suggestion_id,
    price_id,
    suggestionPrice,
    updateItem,
    insertItem,
    hideItem,
}: DeveloperVeItemProps) {
    const {project_id} = useParams<{ project_id: string }>();
    const {accessToken} = useAuthInfo();
    const {toast} = useToast();
    const projectContext = useContext(ProjectContext);
    const userContext = useContext(UserContext);
    const [isDisabled, setIsDisabled] = React.useState<boolean>(false);

    const isNew = !suggestion_id;
    const isMine = suggestionPrice?.suggestion.created_by?.company.id === userContext?.company_id;

    const methods = useForm<VEListItem>();

    // --- Load form data ---
    useEffect(() => {
        methods.reset(defaultVEListItem(DefaultScope(projectContext)?._id));

        if (suggestionPrice) {
            methods.reset(suggestionPrice);
        } else if (accessToken && suggestion_id) {
            fetchWithToken(`/projects/${project_id}/ve/${suggestion_id}`, accessToken as string)
                .then((response) => {
                    methods.reset({
                        suggestion: {...response.suggestion},
                        price: response.prices.length > 0 ? response.prices[0] : {savings_amount: 0, description: " "},
                    });
                })
                .catch((err) => {
                    toast({variant: "destructive", title: "Error", description: err || "Failed to load"});
                });
        }
    }, [suggestion_id, price_id, project_id]);

    // --- Create suggestion (no price) ---
    const onCreateSuggestion = async (data: VEListItem) => {
        setIsDisabled(true);
        postWithToken(
            `/projects/${project_id}/ve/create`,
            JSON.stringify(data.suggestion),
            accessToken as string
        )
            .then((response: VEDetailItem) => {
                const new_data = {
                    suggestion: response.suggestion,
                    price: response.prices.length > 0 ? response.prices[0] : null,
                };
                methods.reset(defaultVEListItem());
                insertItem(new_data);
                toast({title: "Success!", description: "Suggestion created.", variant: "default"});
            })
            .catch((err) => {
                toast({variant: "destructive", title: "Error", description: JSON.stringify(err) || "Could not create suggestion"});
            })
            .finally(() => setIsDisabled(false));
    };

    // --- Update suggestion ---
    const onUpdateSuggestion = async (data: VEListItem) => {
        setIsDisabled(true);
        putWithToken(
            `/projects/${project_id}/ve/${suggestion_id}/suggestion`,
            JSON.stringify(data.suggestion),
            accessToken as string
        )
            .then((response: VESuggestion) => {
                methods.reset({suggestion: response, price: data.price});
                updateItem({suggestion: response, price: data.price});
                toast({title: "Success!", description: "Suggestion updated.", variant: "default"});
            })
            .catch((err) => {
                toast({variant: "destructive", title: "Error", description: err || "Could not update suggestion"});
            })
            .finally(() => setIsDisabled(false));
    };

    // --- Delete suggestion ---
    const onDeleteSuggestion = async () => {
        if (!(accessToken && suggestion_id)) return;
        deleteWithToken(`/projects/${project_id}/ve/${suggestion_id}`, accessToken)
            .then(() => {
                toast({title: "Deleted", description: "Suggestion removed.", variant: "default"});
            })
            .catch((err) => {
                toast({variant: "destructive", title: "Error", description: err || "Could not delete suggestion"});
            });
    };

    // --- Submit handler ---
    const onSubmit: SubmitHandler<VEListItem> = async (data) => {
        if (isNew) {
            await onCreateSuggestion(data);
        } else {
            await onUpdateSuggestion(data);
        }
    };

    return (
        <Card>
            <CardHeader>
                <CardTitle>
                    <div className="flex justify-between">
                        <div>VE Suggestion</div>
                        {isMine && !isNew && (
                            <Button variant="outline" onClick={onDeleteSuggestion}>
                                <TrashIcon/>
                            </Button>
                        )}
                    </div>
                </CardTitle>
            </CardHeader>
            <CardContent>
                <FormProvider {...methods}>
                    <form onSubmit={methods.handleSubmit(onSubmit)}>
                        <Separator className="m-3"/>

                        {/* Suggestion form — editable when new or mine */}
                        <SuggestionForm
                            entity={suggestionPrice}
                            disabled={!isNew && !isMine}
                            hidden={false}
                        />

                        {/* "Suggested by" line — only when viewing existing */}
                        {suggestionPrice && <SuggestedByLine entity={suggestionPrice}/>}

                        <Separator className="m-3"/>

                        {/* Price — read-only, only shown when a builder has priced */}
                        {suggestionPrice?.price && (
                            <PriceForm
                                entity={suggestionPrice}
                                show_title={true}
                                disabled={true}
                                hidden={false}
                            />
                        )}

                        <br/>
                        <Button type="submit" disabled={isDisabled}>Save</Button>
                    </form>
                </FormProvider>
            </CardContent>
        </Card>
    );
}
